import React, { useState, useEffect } from 'react';
import { Modal, Steps, Form, Input, Select, Switch, Button, Divider, Typography, Alert, Card, Tag, InputNumber, message, Space, Checkbox } from 'antd';
import { PlusOutlined, SettingOutlined, DollarOutlined, ApiOutlined, CheckCircleOutlined, LoadingOutlined, FallOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// 固定类型映射
const TYPE_MAPPING = {
  text: 'Text / 对话',
  image: 'Image / 图像',
  embeddings: 'Embeddings / 嵌入',
  audio: 'Audio / 语音',
  video: 'Video / 视频',
  rerank: 'Rerank / 重排序',
  speech: 'Speech / 语音合成',
  transcription: 'Transcription / 语音识别',
};

// TransformConfig 预设模板（通用名称 + 备注说明）
const PRESET_TEMPLATES = {
  // ==================== 同步模式 ====================
  sync_passthrough: {
    name: '同步 - 请求透传',
    desc: '请求原封不动转发给上游API，响应直接返回。适用于与OpenAI格式完全兼容的API',
    config: ''
  },
  sync_openai: {
    name: '同步 - 标准格式转换',
    desc: '将请求转换为标准格式，支持字段映射和默认值填充。大多数同步API可基于此模板修改。注意：body_template 中的 {变量} 在实际请求时会被替换，测试时需要填写真实值',
    config: {
      request: {
        method: 'POST',
        url_template: '{API_BASE}/chat/completions',
        headers_template: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer {API_KEY}'
        },
        body_template: {
          model: '{MODEL}',
          messages: [{ role: 'user', content: 'Hello' }],
          temperature: 0.7,
          max_tokens: 1024,
          stream: false
        }
      },
      response: {
        result_field: 'choices.0.message.content'
      }
    }
  },
  sync_custom_auth: {
    name: '同步 - 自定义认证',
    desc: '同步API但需要非标准认证方式（如放在请求头、查询参数或请求体中）。示例：使用 x-api-key 认证',
    config: {
      request: {
        method: 'POST',
        headers_template: {
          'x-api-key': '{API_KEY}',
          'Custom-Header': 'custom-value'
        },
        body_template: {
          model: '{MODEL}',
          messages: '{messages}'
        }
      },
      response: {
        result_field: 'result'
      }
    }
  },

  // ==================== 异步模式 - 轮询类 ====================
  async_poll_fixed: {
    name: '异步 - 固定URL轮询',
    desc: '提交任务后，按固定规则拼接轮询URL（如 /tasks/{task_id}）。适用于任务ID明确且轮询路径固定的API',
    config: {
      request: {
        method: 'POST',
        url_template: '{API_BASE}/v1/generate',
        headers_template: {
          'Authorization': 'Bearer {API_KEY}'
        },
        body_template: {
          model: '{MODEL}',
          prompt: '{prompt}'
        }
      },
      response: {
        task_id_field: 'task_id',
        status_field: 'status',
        url_field: 'result.url'
      },
      async: {
        enabled: true,
        mode: 'polling_fixed',
        polling_url_template: '{API_BASE}/v1/tasks/{task_id}',
        status_field: 'status',
        success_status: ['completed', 'success', 'succeeded'],
        failed_status: ['failed', 'error', 'cancelled'],
        max_polls: 60,
        poll_interval: 3,
        timeout: 300
      }
    }
  },
  async_poll_dynamic: {
    name: '异步 - 动态URL轮询',
    desc: '提交任务后，响应中直接返回轮询URL（polling_url）。适用于轮询地址不固定或包含签名的API',
    config: {
      request: {
        method: 'POST',
        url_template: '{API_BASE}/v1/submit',
        headers_template: {
          'Authorization': 'Bearer {API_KEY}'
        },
        body_template: {
          model: '{MODEL}',
          input: '{prompt}'
        }
      },
      response: {
        task_id_field: 'id',
        status_field: 'status',
        url_field: 'output.url',
        polling_url_field: 'polling_url'
      },
      async: {
        enabled: true,
        mode: 'polling_url',
        polling_url_field: 'polling_url',
        status_field: 'status',
        success_status: ['completed', 'success'],
        failed_status: ['failed', 'error'],
        max_polls: 60,
        poll_interval: 5,
        timeout: 600
      }
    }
  },
  async_poll_status_only: {
    name: '异步 - 仅状态轮询',
    desc: '提交任务后，需要多次轮询状态接口，但最终结果通过另一个接口获取。适用于两阶段异步API',
    config: {
      request: {
        method: 'POST',
        url_template: '{API_BASE}/v1/jobs',
        body_template: {
          model: '{MODEL}',
          content: '{prompt}'
        }
      },
      response: {
        task_id_field: 'job_id',
        status_field: 'state'
      },
      async: {
        enabled: true,
        mode: 'polling_fixed',
        polling_url_template: '{API_BASE}/v1/jobs/{task_id}',
        status_field: 'state',
        success_status: ['finished'],
        failed_status: ['failed'],
        max_polls: 120,
        poll_interval: 2,
        timeout: 600
      }
    }
  },

  // ==================== 异步模式 - Webhook类 ====================
  async_webhook: {
    name: '异步 - Webhook回调',
    desc: '提交任务时提供回调URL，任务完成后API主动通知。适用于长时间任务（几分钟~几小时）',
    config: {
      request: {
        method: 'POST',
        url_template: '{API_BASE}/v1/create',
        headers_template: {
          'Authorization': 'Bearer {API_KEY}'
        },
        body_template: {
          model: '{MODEL}',
          prompt: '{prompt}',
          callback_url: '{CALLBACK_URL}'
        }
      },
      response: {
        task_id_field: 'task_id',
        status_field: 'status'
      },
      async: {
        enabled: true,
        mode: 'webhook',
        webhook_url_field: 'callback_url',
        success_status: ['completed', 'success'],
        failed_status: ['failed', 'error'],
        timeout: 1800
      }
    }
  },

  // ==================== 异步模式 - 流式类 ====================
  async_sse: {
    name: '异步 - SSE流式返回',
    desc: '使用 Server-Sent Events 流式返回进度和结果。适用于需要实时显示生成进度的场景（如图像生成）',
    config: {
      request: {
        method: 'POST',
        url_template: '{API_BASE}/v1/generate/stream',
        headers_template: {
          'Authorization': 'Bearer {API_KEY}'
        },
        body_template: {
          model: '{MODEL}',
          prompt: '{prompt}'
        }
      },
      response: {
        task_id_field: 'task_id',
        status_field: 'status',
        stream_field: 'data',
        stream_done_flag: '[DONE]'
      },
      async: {
        enabled: true,
        mode: 'sse',
        sse_url_template: '{API_BASE}/v1/stream/{task_id}',
        timeout: 300
      }
    }
  },
  async_websocket: {
    name: '异步 - WebSocket双向通信',
    desc: '通过WebSocket建立长连接，双向实时通信。适用于需要频繁交互或实时反馈的场景',
    config: {
      request: {
        method: 'POST',
        url_template: '{API_BASE}/v1/ws/connect',
        headers_template: {
          'Authorization': 'Bearer {API_KEY}'
        },
        body_template: {
          model: '{MODEL}',
          session_id: '{SESSION_ID}'
        }
      },
      response: {
        task_id_field: 'session_id',
        status_field: 'connection_status'
      },
      async: {
        enabled: true,
        mode: 'websocket',
        websocket_url_template: 'ws://{API_BASE}/v1/ws/{task_id}',
        timeout: 3600
      }
    }
  },

  // ==================== 异步模式 - 其他类 ====================
  async_redirect: {
    name: '异步 - 重定向获取结果',
    desc: '提交任务后，API返回重定向地址（HTTP 302），客户端需要跳转获取结果。适用于文件生成类API',
    config: {
      request: {
        method: 'POST',
        url_template: '{API_BASE}/v1/export',
        body_template: {
          model: '{MODEL}',
          format: 'pdf',
          content: '{prompt}'
        }
      },
      response: {
        task_id_field: 'request_id',
        url_field: 'redirect_url',
        status_field: 'status'
      },
      async: {
        enabled: true,
        mode: 'redirect',
        url_field: 'redirect_url',
        success_status: ['ready'],
        failed_status: ['failed'],
        timeout: 300
      }
    }
  },
  async_long_polling: {
    name: '异步 - 长轮询（Long Polling）',
    desc: '客户端发起请求后，服务器保持连接直到有结果或超时。减少轮询次数，实时性更好',
    config: {
      request: {
        method: 'GET',
        url_template: '{API_BASE}/v1/result/long-poll?task_id={TASK_ID}',
        headers_template: {
          'Authorization': 'Bearer {API_KEY}'
        }
      },
      response: {
        task_id_field: 'task_id',
        status_field: 'status',
        result_field: 'data'
      },
      async: {
        enabled: true,
        mode: 'long_polling',
        timeout: 60
      }
    }
  }
};

const ModelWizard = ({ visible, onCancel, onSuccess, editingModel = null, providers = [] }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [extraParams, setExtraParams] = useState([]);
  const [transformConfig, setTransformConfig] = useState('');
  const [transformConfigError, setTransformConfigError] = useState('');
  const [fallbackModels, setFallbackModels] = useState({
    text: [], image: [], embeddings: [], audio: [], video: [], rerank: [], speech: [], transcription: []
  });
  const [selectedFallbackType, setSelectedFallbackType] = useState('text'); // 当前选中的备用模型类型
  const [allModels, setAllModels] = useState([]); // 所有模型列表用于选择备用模型

  const isEdit = !!editingModel;
  const billingType = editingModel?.billing_type || 'per_token';
  const effectiveRate = editingModel?.billing_rate > 0 ? editingModel.billing_rate : (editingModel?.input_price || 0);

  // 获取所有模型列表
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await adminAPI.getModelConfigs();
        if (response.code === 0) {
          setAllModels(response.data?.list || []);
        }
      } catch (e) {}
    };
    if (visible) {
      fetchModels();
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      setCurrentStep(0);
      setTestResult(null);
      if (editingModel) {
        form.setFieldsValue({
          ...editingModel,
          provider_id: String(editingModel.provider_id || ''),
          use_case: editingModel.use_case?.split(',') || [],
          capabilities: editingModel.capabilities?.split(',') || [],
          billing_rate: editingModel.billing_rate > 0 ? editingModel.billing_rate : (editingModel.input_price || 0),
        });
        // Parse extra params
        if (editingModel.default_params) {
          try {
            const paramsObj = JSON.parse(editingModel.default_params);
            const params = Object.entries(paramsObj).map(([key, value]) => ({
              key,
              value: typeof value === 'object' ? JSON.stringify(value) : String(value),
              isObject: typeof value === 'object',
            }));
            setExtraParams(params);
          } catch (e) {}
        }
        // Parse transform_config - 处理双重转义的情况
        if (editingModel.transform_config) {
          try {
            const raw = editingModel.transform_config;
            let config;
            if (typeof raw === 'string') {
              // 尝试直接解析，如果失败则处理双重转义
              try {
                config = JSON.parse(raw);
              } catch {
                // 如果失败，可能是双重转义，尝试处理
                const unescaped = raw.replace(/\\n/g, '\n').replace(/\\"/g, '"');
                config = JSON.parse(unescaped);
              }
            } else {
              config = raw;
            }
            setTransformConfig(JSON.stringify(config, null, 2));
          } catch (e) {
            console.error('Parse transform_config error:', e);
            setTransformConfig(typeof editingModel.transform_config === 'string'
              ? editingModel.transform_config
              : '');
          }
        } else {
          setTransformConfig('');
        }
        // Parse fallback_models
        if (editingModel.fallback_models) {
          try {
            const fb = typeof editingModel.fallback_models === 'string'
              ? JSON.parse(editingModel.fallback_models)
              : editingModel.fallback_models;
            setFallbackModels({
              text: fb.text || [],
              image: fb.image || [],
              embeddings: fb.embeddings || [],
              audio: fb.audio || [],
              video: fb.video || [],
              rerank: fb.rerank || [],
              speech: fb.speech || [],
              transcription: fb.transcription || [],
            });
          } catch (e) {
            setFallbackModels({ text: [], image: [], embeddings: [], audio: [], video: [], rerank: [], speech: [], transcription: [] });
          }
        } else {
          setFallbackModels({ text: [], image: [], embeddings: [], audio: [], video: [], rerank: [], speech: [], transcription: [] });
        }
      } else {
        form.resetFields();
        setExtraParams([]);
        setTransformConfig('');
        setFallbackModels({ text: [], image: [], embeddings: [], audio: [], video: [], rerank: [], speech: [], transcription: [] });
      }
    }
  }, [visible, editingModel]);

  const steps = [
    { title: '基础信息', icon: <PlusOutlined />, description: '配置基本信息' },
    { title: '计费配置', icon: <DollarOutlined />, description: '设置计费方式' },
    { title: '高级配置', icon: <SettingOutlined />, description: '高级参数配置' },
    { title: '测试模型', icon: <ApiOutlined />, description: '验证可用性' },
    { title: '确认保存', icon: <CheckCircleOutlined />, description: '确认并保存' },
  ];

  const providerOptions = providers.filter(p => p.status === 'active').map(p => ({
    value: String(p.id),
    label: p.display_name || p.name || `供应商#${p.id}`,
  }));

  const buildExtraParamsJSON = () => {
    if (extraParams.length === 0) return '';
    const paramsObj = {};
    extraParams.forEach(p => {
      if (p.key === 'extra_body') {
        try { paramsObj[p.key] = JSON.parse(p.value); } catch (e) { paramsObj[p.key] = p.value; }
      } else if (!isNaN(Number(p.value))) {
        paramsObj[p.key] = Number(p.value);
      } else {
        paramsObj[p.key] = p.value;
      }
    });
    return JSON.stringify(paramsObj, null, 2);
  };

  const addExtraParam = (key, value) => {
    if (!key) return;
    const exists = extraParams.some(p => p.key === key);
    if (exists) { message.error('该参数已存在'); return; }
    setExtraParams([...extraParams, { key, value: String(value), isObject: key === 'extra_body' }]);
  };

  const removeExtraParam = (index) => {
    const newParams = [...extraParams];
    newParams.splice(index, 1);
    setExtraParams(newParams);
  };

  const handleTest = async () => {
    try {
      // 获取所有步骤的字段值（不能用 validateFields，因为只验证当前步骤）
      const values = form.getFieldsValue(true);

      // 手动校验必填字段
      if (!values.provider_id) {
        message.error('请先在第一步选择供应商');
        setCurrentStep(0);
        return;
      }
      if (!values.model_name) {
        message.error('请先填写模型标识');
        setCurrentStep(0);
        return;
      }

      setTesting(true);
      setTestResult(null);

      const testData = {
        provider_id: String(values.provider_id || ''),
        model_name: values.model_name,
        display_name: values.display_name || '',
        billing_type: values.billing_type || 'per_token',
        input_price: values.input_price || 0,
        output_price: values.output_price || 0,
        default_params: buildExtraParamsJSON(),
      };

      // 添加 transform_config（空字符串表示透传模式，也会包含）
      if (transformConfig.trim()) {
        try {
          JSON.parse(transformConfig);
          testData.transform_config = transformConfig.trim();
        } catch (e) {
          message.error('TransformConfig JSON 格式错误：' + e.message);
          setTesting(false);
          return;
        }
      } else {
        // 空字符串表示透传模式
        testData.transform_config = '';
      }

      // 添加备用模型配置（仅模型级别的 fallback）
      const hasFallback = Object.values(fallbackModels).some(v => v && v.length > 0);
      if (hasFallback) {
        testData.fallback_models = JSON.stringify(fallbackModels);
      }

      const result = await adminAPI.testModelConfig(testData);
      console.log('[TestModel] API result:', result);

      // 响应拦截器已返回 response.data
      // result = { code, message, data }  ← 外层包装
      // result.data = { code, message, data }  ← 内层测试结果
      // result.data.data = { request: {...}, response: {...} }  ← 实际请求/响应详情
      const testDetail = result.data?.data || {};
      const testSuccess = testDetail?.response?.status >= 200 && testDetail?.response?.status < 300;

      setTestResult({
        success: testSuccess,
        message: result.data?.message || result.message || (testSuccess ? '测试成功' : '测试失败'),
        request: testDetail?.request || {},
        response: testDetail?.response || {},
        fallback_models: hasFallback ? fallbackModels : null,
      });
      console.log('[TestModel] testDetail:', testDetail);

      if (testSuccess) {
        message.success('测试成功！模型可以正常使用。');
      } else {
        message.error(result.data?.message || result.message || '测试失败');
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: error.message || '测试失败',
        request: {},
        response: { error: error.message },
      });
    } finally {
      setTesting(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      try {
        await form.validateFields(['provider_id', 'model_name', 'display_name']);
        setCurrentStep(currentStep + 1);
      } catch (error) {
        message.warning('请填写完整的必填项');
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => setCurrentStep(currentStep - 1);

  const handleSubmit = async () => {
    try {
      // 使用 getFieldsValue 而不是 validateFields，因为确认步骤没有 Form.Item 在 DOM 中
      const values = form.getFieldsValue(true);
      setLoading(true);

      const billingType = values.billing_type || 'per_token';
      // 外部供应商：模型名自动拼接为 供应商名/模型名
      const selectedProvider = providers.find(p => String(p.id) === String(values.provider_id));
      let modelName = values.model_name;
      if (selectedProvider?.provider_type === 'external' && modelName && !modelName.includes('/')) {
        modelName = `${selectedProvider.name}/${modelName}`;
      }
      const submitData = {
        provider_id: String(values.provider_id),
        model_name: modelName,
        // 上游真实模型名：留空则后端回退为 model_name（转发时用 model_name 替换）
        upstream_model_name: (values.upstream_model_name || '').trim(),
        display_name: values.display_name,
        description: values.description || '',
        icon: values.icon || '',
        type: Array.isArray(values.type) ? values.type.join(',') : (values.type || 'text'),
        use_case: Array.isArray(values.use_case) ? values.use_case.join(',') : '',
        capabilities: Array.isArray(values.capabilities) ? values.capabilities.join(',') : '',
        is_hot: values.is_hot || false,
        billing_type: billingType,
        default_params: buildExtraParamsJSON(),
      };

      // 添加上下文窗口配置（转换为字符串格式如 "4096" 或 "4K"）
      if (values.context_window && values.context_window > 0) {
        const ctx = values.context_window;
        if (ctx >= 1000) {
          submitData.context_length = (ctx / 1000) + 'K';
        } else {
          submitData.context_length = String(ctx);
        }
      }

      // 添加函数调用支持配置
      if (values.tools_support !== undefined) {
        submitData.tools_support = values.tools_support;
      }

      // 添加 transform_config（空字符串表示透传模式，也会保存）
      if (transformConfig.trim()) {
        try {
          // 验证 JSON 格式
          JSON.parse(transformConfig);
          submitData.transform_config = transformConfig.trim();
        } catch (e) {
          message.error('TransformConfig JSON 格式错误：' + e.message);
          return;
        }
      } else {
        // 空字符串表示透传模式，显式保存为空配置
        submitData.transform_config = '';
      }

      // 根据计费方式只提交对应价格字段，其他字段显式清零
      if (billingType === 'per_token') {
        submitData.input_price = parseFloat(values.input_price) || 0;
        submitData.output_price = parseFloat(values.output_price) || 0;
        submitData.billing_rate = 0;
      } else if (billingType === 'per_call' || billingType === 'per_char' || billingType === 'per_second') {
        submitData.billing_rate = parseFloat(values.billing_rate) || 0;
        submitData.input_price = 0;
        submitData.output_price = 0;
      } else {
        // free - 所有价格清零
        submitData.input_price = 0;
        submitData.output_price = 0;
        submitData.billing_rate = 0;
      }

      if (isEdit) {
        submitData.adapter_type = values.adapter_type || '';
        submitData.api_path = values.api_path || '';
        submitData.api_base = values.api_base || '';
        submitData.status = values.status || 'active';
      }

      // 添加备用模型配置
      const hasFallback = Object.values(fallbackModels).some(v => v && v.length > 0);
      if (hasFallback) {
        submitData.fallback_models = JSON.stringify(fallbackModels);
      } else {
        submitData.fallback_models = '';
      }

      let response;
      if (isEdit) {
        response = await adminAPI.updateModelConfig(editingModel.id, submitData);
      } else {
        response = await adminAPI.createModelConfig(submitData);
      }

      if (response.code === 0) {
        message.success(isEdit ? '更新成功' : '创建成功');
        onSuccess();
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) {
      message.error('操作失败：' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // 基础信息
        return (
          <div>
            <Alert
              message="基础信息配置"
              description="模型标识是调用API时使用的名称，显示名称是展示给用户看的名称。"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item
                name="provider_id"
                label="选择供应商"
                rules={[{ required: true, message: '请选择供应商' }]}
                tooltip="模型所属的AI服务商"
              >
                <Select placeholder="请选择供应商" options={providerOptions} />
              </Form.Item>

              <Form.Item
                name="model_name"
                label="模型标识"
                rules={[{ required: true, message: '请输入模型标识' }]}
                tooltip="调用API时使用的模型名称，如：deepseek-chat"
              >
                <Input placeholder="如: deepseek-chat" disabled={isEdit} />
              </Form.Item>
            </div>

            <Form.Item
              name="upstream_model_name"
              label="上游模型名称"
              tooltip="转发给上游API时替换进去的真实模型名。留空则直接使用上面的「模型标识」"
            >
              <Input placeholder="留空 = 与模型标识相同，如：ZHIPU/GLM-5.3-Flash" />
            </Form.Item>
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: -12, marginBottom: 16 }}>
              只有当<strong>对外模型标识与上游真实模型名不一致</strong>时才需要填。例如对外叫 <code>ali_bj/GLM-5.3-Flash</code>，
              上游实际是 <code>ZHIPU/GLM-5.3-Flash</code>。该名称<strong>不会展示给客户</strong>。
            </Paragraph>

            <Form.Item
              name="display_name"
              label="显示名称"
              rules={[{ required: true, message: '请输入显示名称' }]}
              tooltip="展示给用户看的名称，如：DeepSeek Chat"
            >
              <Input placeholder="如: DeepSeek Chat" />
            </Form.Item>

            <Paragraph type="secondary" style={{ fontSize: 12 }}>
              <strong>如何获取模型标识？</strong><br />
              1. 查看AI服务商文档，找到模型列表<br />
              2. 常见的模型标识：<br />
              &nbsp;&nbsp;- DeepSeek: deepseek-chat, deepseek-reasoner<br />
              &nbsp;&nbsp;- 通义千问: qwen-turbo, qwen-plus, qwen-max<br />
              &nbsp;&nbsp;- 智谱AI: glm-4, glm-4-plus, cogview-3
            </Paragraph>

            <Form.Item name="description" label="模型描述">
              <TextArea rows={2} placeholder="简要描述模型的能力和特点" />
            </Form.Item>

            <Form.Item name="icon" label="模型图标URL" tooltip="模型广场展示的图标，支持 JPG/PNG/SVG 等格式">
              <Input placeholder="如: https://example.com/icon.png（留空则显示默认图标）" />
            </Form.Item>

            {/* 模型能力配置 */}
            <Divider plain style={{ fontWeight: 'bold', color: '#1890ff' }}>模型能力配置</Divider>

            <Card size="small" style={{ backgroundColor: '#f6ffed', marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                配置此模型支持的能力。如果留空或选择"无"，将继承供应商的默认配置。
              </Text>
            </Card>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <Form.Item
                name="capabilities"
                label="模型能力"
                tooltip="选择此模型支持的功能"
              >
                <Checkbox.Group>
                  <Checkbox value="vision" style={{ lineHeight: '32px' }}>视觉理解</Checkbox>
                  <Checkbox value="function_calling" style={{ lineHeight: '32px' }}>函数调用</Checkbox>
                </Checkbox.Group>
              </Form.Item>

              <Form.Item
                name="context_window"
                label="上下文窗口"
                tooltip="此模型的最大上下文窗口大小（tokens）。留空则使用供应商默认值。"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1024}
                  placeholder="4096"
                  formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                  parser={value => value ? value.replace(/,/g, '') : ''}
                />
              </Form.Item>

              <Form.Item
                name="tools_support"
                label="函数调用支持"
                valuePropName="checked"
                tooltip="此模型是否支持函数调用/工具使用"
              >
                <Switch checkedChildren="支持" unCheckedChildren="不支持" />
              </Form.Item>
            </div>
          </div>
        );
      case 1: // 计费配置
        return (
          <div>
            <Alert
              message="计费方式配置"
              description="设置模型的计费方式，系统会根据此配置对用户进行扣费。"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form.Item
              name="billing_type"
              label="计费方式"
              initialValue="per_token"
              rules={[{ required: true }]}
              tooltip="per_token: 按Token计费；per_call: 按次计费；per_char: 按字符计费；free: 免费"
            >
              <Select options={[
                { value: 'per_token', label: '按 Token 计费（文本对话）' },
                { value: 'per_call', label: '按次计费（图像/视频生成）' },
                { value: 'per_char', label: '按字符计费（语音合成 TTS）' },
                { value: 'per_second', label: '按秒计费（语音识别 ASR）' },
                { value: 'free', label: '免费' },
              ]} />
            </Form.Item>

            <Form.Item shouldUpdate={(prev, cur) => prev.billing_type !== cur.billing_type} noStyle>
              {() => {
                const billingType = form.getFieldValue('billing_type');
                return billingType === 'per_token' ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <Form.Item
                      name="input_price"
                      label="输入价格 ($/1K Tokens)"
                      initialValue={0.001}
                      tooltip="每1000个输入Token的价格"
                    >
                      <InputNumber style={{ width: '100%' }} min={0} step={0.0001} placeholder="0.001" />
                    </Form.Item>
                    <Form.Item
                      name="output_price"
                      label="输出价格 ($/1K Tokens)"
                      initialValue={0.002}
                      tooltip="每1000个输出Token的价格"
                    >
                      <InputNumber style={{ width: '100%' }} min={0} step={0.0001} placeholder="0.002" />
                    </Form.Item>
                  </div>
                ) : (
                  <Form.Item
                    name="billing_rate"
                    label={
                      billingType === 'per_call' ? '每次调用价格 ($)' :
                      billingType === 'per_char' ? '每字符价格 ($)' : billingType === 'per_second' ? '每秒价格 ($)' : '价格 ($)'
                    }
                    initialValue={0}
                    tooltip={billingType === 'per_call' ? '每次调用收取的费用' : '每个字符收取的费用'}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} step={0.0001} placeholder="0" />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <strong>计费说明：</strong><br />
                • per_token：根据输入和输出的Token数量计费<br />
                • per_call：每次调用固定费用（适合图像/视频生成）<br />
                • per_char：根据输入字符数量计费（适合语音合成 TTS）<br />
                • per_second：根据音频时长计费（适合语音识别 ASR）<br />
                • free：不收取费用
              </Text>
            </Card>
          </div>
        );

      case 2: // 高级配置
        return (
          <div>
            <Alert
              message="高级配置（可选）"
              description="对于大多数模型，不需要配置此项。仅当模型需要特殊参数时配置。"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Divider plain>默认参数配置</Divider>

            <Card size="small" style={{ backgroundColor: '#f6ffed', marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                提示：这些参数会作为默认值合并到用户请求中。用户请求中的参数会覆盖默认值。
              </Text>

              {/* 预设参数快速添加 */}
              <div style={{ marginTop: 12, marginBottom: 12 }}>
                <Text strong style={{ fontSize: 13 }}>快速添加预设参数：</Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {[
                    { key: 'temperature', label: 'temperature', defaultVal: 0.7 },
                    { key: 'max_tokens', label: 'max_tokens', defaultVal: 4096 },
                    { key: 'top_p', label: 'top_p', defaultVal: 1.0 },
                  ].map(param => (
                    <Tag
                      key={param.key}
                      style={{ cursor: 'pointer' }}
                      onClick={() => addExtraParam(param.key, String(param.defaultVal))}
                      color="green"
                    >
                      + {param.label}
                    </Tag>
                  ))}
                </div>
              </div>

              {/* 已配置参数列表 */}
              {extraParams.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <Text strong style={{ fontSize: 13 }}>已配置参数：</Text>
                  {extraParams.map((param, index) => (
                    <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                      <code style={{ backgroundColor: '#e6f7ff', padding: '2px 4px', borderRadius: 3, fontSize: 12 }}>
                        {param.key}
                      </code>
                      <Input
                        size="small"
                        value={param.value}
                        onChange={(e) => {
                          const newParams = [...extraParams];
                          newParams[index].value = e.target.value;
                          setExtraParams(newParams);
                        }}
                        style={{ fontSize: 12, flex: 1 }}
                      />
                      <Button
                        type="text"
                        danger
                        size="small"
                        onClick={() => removeExtraParam(index)}
                      >
                        删除
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* 添加自定义参数 */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Input
                  placeholder="参数名"
                  style={{ width: 150 }}
                  size="small"
                  id="newParamKey"
                />
                <Input
                  placeholder="默认值"
                  style={{ width: 100 }}
                  size="small"
                  id="newParamValue"
                />
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    const keyInput = document.getElementById('newParamKey');
                    const valueInput = document.getElementById('newParamValue');
                    if (keyInput && valueInput && keyInput.value) {
                      addExtraParam(keyInput.value, valueInput.value);
                      keyInput.value = '';
                      valueInput.value = '';
                    }
                  }}
                >
                  添加
                </Button>
              </div>

              {extraParams.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>参数预览：</Text>
                  <pre style={{ backgroundColor: '#fff', padding: 8, borderRadius: 4, fontSize: 11, marginTop: 4 }}>
                    {buildExtraParamsJSON()}
                  </pre>
                </div>
              )}
            </Card>

            {/* TransformConfig 配置 */}
            <Divider plain>TransformConfig 配置（可选）</Divider>

            <Card size="small" style={{ marginBottom: 16 }}>
              {/* 预设模板选择器 */}
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 13, marginRight: 8, display: 'block', marginBottom: 8 }}>预设模板：</Text>
                <Select
                  style={{ width: '100%' }}
                  placeholder="选择预设模板（按类型筛选）"
                  allowClear={true}
                  showSearch={true}
                  optionFilterProp="label"
                  optionLabelProp="title"
                  onChange={(value) => {
                    if (value && PRESET_TEMPLATES[value]) {
                      const config = PRESET_TEMPLATES[value].config;
                      setTransformConfig(typeof config === 'string' ? config : JSON.stringify(config, null, 2));
                      setTransformConfigError('');
                    }
                  }}
                  popupMatchSelectWidth={false}
                  listHeight={320}
                  options={Object.entries(PRESET_TEMPLATES).map(([key, val]) => ({
                    value: key,
                    title: val.name,
                    label: (
                      <div style={{ minWidth: 420, padding: '4px 0' }}>
                        <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 2 }}>{val.name}</div>
                        <div style={{ fontSize: 11, color: '#888', lineHeight: 1.4, whiteSpace: 'normal', wordBreak: 'break-all' }}>{val.desc}</div>
                      </div>
                    )
                  }))}
                />
              </div>

              {/* JSON 编辑器 */}
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>TransformConfig JSON：</Text>
                <TextArea
                  value={transformConfig}
                  onChange={(e) => {
                    setTransformConfig(e.target.value);
                    try {
                      if (e.target.value.trim()) {
                        JSON.parse(e.target.value);
                      }
                      setTransformConfigError('');
                    } catch (err) {
                      setTransformConfigError('JSON 格式错误：' + err.message);
                    }
                  }}
                  placeholder='在此输入或粘贴 TransformConfig JSON，或选择预设模板...'
                  autoSize={{ minRows: 10, maxRows: 20 }}
                  status={transformConfigError ? 'error' : ''}
                />
                {transformConfigError && (
                  <Text type="danger" style={{ fontSize: 12 }}>{transformConfigError}</Text>
                )}
              </div>

              {/* 帮助文本 */}
              <Text type="secondary" style={{ fontSize: 12 }}>
                TransformConfig 用于配置非 OpenAI 兼容的 API。留空则使用默认 OpenAI 兼容模式。<br />
                选择上方"预设模板"可快速填充配置。点击"确认保存"步骤可预览完整配置。
              </Text>
            </Card>

            {/* 备用模型配置 */}
            <Divider plain><FallOutlined /> 备用模型配置</Divider>

            <Card size="small" style={{ backgroundColor: '#fff7e6', marginBottom: 16 }}>
              <Alert
                message="备用模型说明"
                description={
                  <span>当此模型不可用时，系统会按优先级尝试备用模型：用户级 &gt; 模型级 &gt; 平台默认</span>
                }
                type="info"
                style={{ marginBottom: 16 }}
              />

              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 13 }}>选择模型类型：</Text>
                <Select
                  style={{ width: 200, marginLeft: 8 }}
                  placeholder="选择类型"
                  value={selectedFallbackType}
                  onChange={(value) => setSelectedFallbackType(value)}
                >
                  {Object.entries(TYPE_MAPPING).map(([key, label]) => (
                    <Select.Option key={key} value={key}>{label}</Select.Option>
                  ))}
                </Select>
              </div>

              <div>
                <Text strong style={{ fontSize: 13 }}>{TYPE_MAPPING[selectedFallbackType] || selectedFallbackType} 备用模型：</Text>
                <Select
                  mode="tags"
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder="选择或输入备用模型名称"
                  value={fallbackModels[selectedFallbackType] || []}
                  onChange={(value) => setFallbackModels({ ...fallbackModels, [selectedFallbackType]: value })}
                >
                  {allModels.filter(m => m.type === selectedFallbackType && m.status === 'active' && m.id !== editingModel?.id).map(m => (
                    <Select.Option key={m.model_name} value={m.model_name}>
                      {m.display_name || m.model_name} ({m.provider?.display_name || ''})
                    </Select.Option>
                  ))}
                </Select>
              </div>

              {/* 当前配置预览 */}
              {Object.values(fallbackModels).some(v => v && v.length > 0) && (
                <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                  <Text strong style={{ fontSize: 13 }}>当前配置预览：</Text>
                  {Object.entries(fallbackModels).filter(([_, v]) => v && v.length > 0).map(([type, models]) => (
                    <div key={type} style={{ marginTop: 4 }}>
                      <Text type="secondary">{TYPE_MAPPING[type] || type}: </Text>
                      <Text>{models.join(', ')}</Text>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        );

      case 3: // 测试模型
        return (
          <div>
            <Alert
              message="测试模型"
              description="点击测试按钮，系统将使用您配置的模型发送一个测试请求，验证模型是否可用。测试仅消耗极少token（约5-10个）。"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Card size="small" style={{ marginBottom: 24 }}>
              <p><strong>供应商：</strong> {providers.find(p => String(p.id) === String(form.getFieldValue('provider_id')))?.display_name}</p>
              <p><strong>模型标识：</strong> {form.getFieldValue('model_name')}</p>
              <p><strong>上游模型名称：</strong> {form.getFieldValue('upstream_model_name')?.trim() || <Text type="secondary">（未填，将使用模型标识）</Text>}</p>
              <p><strong>显示名称：</strong> {form.getFieldValue('display_name')}</p>
              <p><strong>计费方式：</strong> {form.getFieldValue('billing_type')}</p>
            </Card>

            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Button
                type="primary"
                size="large"
                icon={testing ? <LoadingOutlined /> : <ApiOutlined />}
                onClick={handleTest}
                loading={testing}
                disabled={!form.getFieldValue('provider_id') || !form.getFieldValue('model_name')}
              >
                {testing ? '测试中...' : '开始测试'}
              </Button>
            </div>

            {testResult && (
              <div>
                <Alert
                  message={testResult.success ? '✅ 测试成功' : '❌ 测试失败'}
                  description={testResult.message}
                  type={testResult.success ? 'success' : 'error'}
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <Divider>请求详情</Divider>
                <Card size="small" title="请求信息" style={{ marginBottom: 16 }}>
                  <p><strong>URL: </strong> {testResult.request?.url}</p>
                  <p><strong>Method: </strong> {testResult.request?.method}</p>
                </Card>
                <Card size="small" title="请求头 (Headers)" style={{ marginBottom: 16 }}>
                  <pre style={{ backgroundColor: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 12, overflow: 'auto' }}>
                    {JSON.stringify(testResult.request?.headers || {}, null, 2)}
                  </pre>
                </Card>

                <Card size="small" title="请求体 (Body)" style={{ marginBottom: 16 }}>
                  <pre style={{ backgroundColor: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 12, overflow: 'auto' }}>
                    {JSON.stringify(testResult.request?.body || {}, null, 2)}
                  </pre>
                </Card>

                {testResult.transform_config && (
                  <Card size="small" title="TransformConfig 应用预览" style={{ marginBottom: 16, borderColor: '#1890ff' }}>
                    <p style={{ color: '#1890ff', fontSize: 12, marginBottom: 8 }}>✓ TransformConfig 已包含在测试中</p>
                    <pre style={{ backgroundColor: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 11, overflow: 'auto', maxHeight: 300 }}>
                      {JSON.stringify(testResult.transform_config, null, 2)}
                    </pre>
                  </Card>
                )}

                {testResult.fallback_models && (
                  <Card size="small" title="模型级备用模型配置" style={{ marginBottom: 16, borderColor: '#fa8c16' }}>
                    <p style={{ color: '#fa8c16', fontSize: 12, marginBottom: 8 }}>✓ 备用模型配置已包含在测试中（仅测试主模型，备用模型在调用失败时生效）</p>
                    {Object.entries(testResult.fallback_models).filter(([_, v]) => v && v.length > 0).map(([type, models]) => (
                      <p key={type} style={{ marginBottom: 4 }}>
                        <strong>{TYPE_MAPPING[type] || type}：</strong>
                        {models.join(', ')}
                      </p>
                    ))}
                    <Alert
                      message="备用模型测试说明"
                      description="主模型测试通过后，备用模型会在主模型调用失败或触发 fallback 条件时自动切换。备用模型本身的可用性需要单独测试。"
                      type="info"
                      style={{ marginTop: 8 }}
                    />
                  </Card>
                )}

                <Divider>响应详情</Divider>
                <Card size="small" title="响应状态码" style={{ marginBottom: 16 }}>
                  <Tag color={testResult.response?.status < 400 ? 'green' : 'red'}>
                    {testResult.response?.status} {testResult.response?.status_text}
                  </Tag>
                  {testResult.response?.response_time_ms && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      响应时间: {testResult.response.response_time_ms}ms
                    </Text>
                  )}
                </Card>

                <Card size="small" title="响应体 (Response Body)">
                  <pre style={{ backgroundColor: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 12, overflow: 'auto', maxHeight: 300 }}>
                    {JSON.stringify(testResult.response?.data || testResult.response?.raw || testResult.response?.error || {}, null, 2)}
                  </pre>
                </Card>
              </div>
            )}

            {!testResult && (
              <Alert
                message="测试说明"
                description={
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    <li>测试将使用您的API Key发送一个简单请求</li>
                    <li>不会消耗您的额度（或只消耗极少额度）</li>
                    <li>如果测试失败，请检查：模型标识、API Key、网络连通性</li>
                    <li>测试成功后，您可以继续下一步保存配置</li>
                  </ul>
                }
                type="info"
                showIcon
              />
            )}
          </div>
        );

      case 4: // 确认保存
        return (
          <div>
            <Alert
              message="确认配置信息"
              description="请仔细核对以下配置信息，确认无误后点击保存。"
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Card size="small" title="基础信息">
              <p><strong>供应商：</strong> {providers.find(p => String(p.id) === String(form.getFieldValue('provider_id')))?.display_name}</p>
              <p><strong>模型标识：</strong> {form.getFieldValue('model_name')}</p>
              <p><strong>上游模型名称：</strong> {form.getFieldValue('upstream_model_name')?.trim() || <Text type="secondary">（未填，将使用模型标识）</Text>}</p>
              <p><strong>显示名称：</strong> {form.getFieldValue('display_name')}</p>
              <p><strong>描述：</strong> {form.getFieldValue('description') || '无'}</p>
            </Card>

            <Card size="small" title="计费信息" style={{ marginTop: 16 }}>
              <p><strong>计费方式：</strong> {form.getFieldValue('billing_type')}</p>
              {form.getFieldValue('billing_type') === 'per_token' && (
                <p><strong>价格：</strong> 输入 ${parseFloat(form.getFieldValue('input_price') || 0).toFixed(4)} / 输出 ${parseFloat(form.getFieldValue('output_price') || 0).toFixed(4)} / 1K Tokens</p>
              )}
              {form.getFieldValue('billing_type') === 'per_call' && (
                <p><strong>价格：</strong> ${parseFloat(form.getFieldValue('billing_rate') || 0).toFixed(4)} / 次</p>
              )}
              {form.getFieldValue('billing_type') === 'per_char' && (
                <p><strong>价格：</strong> ${parseFloat(form.getFieldValue('billing_rate') || 0).toFixed(6)} / 字符</p>
              )}
              {form.getFieldValue('billing_type') === 'per_second' && (
                <p><strong>价格：</strong> ${parseFloat(form.getFieldValue('billing_rate') || 0).toFixed(6)} / 秒</p>
              )}
            </Card>

            {testResult && (
              <Card size="small" title="测试结果" style={{ marginTop: 16 }}>
                <p>
                  <strong>状态：</strong>
                  <span style={{ color: testResult.success ? '#52c41a' : '#ff4d4f' }}>
                    {testResult.success ? '✅ 测试成功' : '❌ 测试失败'}
                  </span>
                </p>
                {testResult.success && (
                  <p style={{ color: '#52c41a' }}>配置验证通过，可以安全保存。</p>
                )}
              </Card>
            )}

            {/* TransformConfig 预览 */}
            {transformConfig.trim() && (
              <Card size="small" title="TransformConfig 配置" style={{ marginTop: 16 }}>
                <pre style={{ backgroundColor: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 11, overflow: 'auto', maxHeight: 300 }}>
                  {transformConfig}
                </pre>
              </Card>
            )}

            {/* 备用模型预览 */}
            {Object.values(fallbackModels).some(v => v && v.length > 0) && (
              <Card size="small" title="备用模型配置" style={{ marginTop: 16 }}>
                {Object.entries(fallbackModels).filter(([_, v]) => v && v.length > 0).map(([type, models]) => (
                  <p key={type}>
                    <strong>{TYPE_MAPPING[type] || type}：</strong>
                    {models.join(', ')}
                  </p>
                ))}
              </Card>
            )}

            {!testResult && (
              <Alert
                message="未测试模型"
                description="建议您先测试模型，确保配置正确后再保存。"
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
                action={
                  <Button size="small" onClick={() => setCurrentStep(3)}>
                    去测试
                  </Button>
                }
              />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <span>
          <PlusOutlined style={{ marginRight: 8 }} />
          {isEdit ? '编辑模型' : '添加模型向导'}
        </span>
      }
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={onCancel}>取消</Button>
          <div>
            {currentStep > 0 && (
              <Button style={{ marginRight: 8 }} onClick={handlePrev}>
                上一步
              </Button>
            )}
            {currentStep < steps.length - 1 && (
              <Button type="primary" onClick={handleNext}>
                下一步
              </Button>
            )}
            {currentStep === steps.length - 1 && (
              <Button type="primary" onClick={handleSubmit} loading={loading}>
                保存
              </Button>
            )}
          </div>
        </div>
      }
    >
      <Steps
        current={currentStep}
        items={steps}
        style={{ marginBottom: 32 }}
      />

      <Form form={form} layout="vertical">
        {renderStepContent()}
      </Form>
    </Modal>
  );
};

export default ModelWizard;
